import React, { createContext, useState, useContext, useEffect, useMemo } from "react";
import axios from "axios";
import { getToken } from "../utils/tokenUtils";

// Create the ApprovalLevelMasterContext
const ApprovalLevelMasterContext = createContext();

// Custom hook to use the ApprovalLevelMasterContext
export const useApprovalLevel = () => useContext(ApprovalLevelMasterContext);

// Provider component to wrap the app
export const ApprovalLevelMasterProvider = ({ children }) => {
  const [approvalLevels, setApprovalLevel] = useState([]);
  const API_URL = "https://hrmsnew.prepstripe.com/api/masters/approval-levels"; 
  const token = getToken();
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    perPage: 10,
  });
  const [isLoading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchApprovalLevel = async (page = 1) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}?page=${page}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      // setDepartments(response.data?.data || {}); // Access `data.data` for actual approver module master
      const { data, current_page, last_page, per_page, total } = response.data;

      setApprovalLevel(data.data || []); // Update leave types list

      setPagination({
          currentPage: current_page,
          totalPages: last_page,
          totalItems: total,
          perPage: per_page,
      });
      setLoading(false);
    } catch (error) {
      console.error("Error fetching approver module master:", error);
      setError(error.response?.data?.message || "Failed to fetch approver module.");
    } finally {
      setLoading(false);
    }
  };

  // Fetch departments master from the API on load
  useEffect(() => {
    fetchApprovalLevel();
  }, [API_URL, token]);

  // Add a new approver module
  const addApprovalLevel = async (newApprovalLevel) => {
    try {
      const response = await axios.post(API_URL, newApprovalLevel, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      // setApprovalLevel((prev) => [...prev, response.data?.data]);
      setApprovalLevel((prev) => [response.data?.data, ...prev]);
    } catch (error) {
      console.error("Error adding approver module:", error);
    }
  };

  // Update an existing approver module
  const updateApprovalLevel = async (updateApprovalLevel) => {
    try {
      const response = await axios.put(`${API_URL}/${updateApprovalLevel.id}`, updateApprovalLevel, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setApprovalLevel((prev) =>
        prev.map((approvalLevel) =>
          approvalLevel.id === updateApprovalLevel.id ? { ...approvalLevel, ...response.data?.data } : approvalLevel
        )
      );
    } catch (error) {
      console.error("Error updating approver Module master:", error);
    }
  };

  // Delete a approver module
  const deleteApprovalLevel = async (id) => {
    try {
      await axios.delete(`${API_URL}/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setApprovalLevel((prev) => prev.filter((approvalLevel) => approvalLevel.id !== id));
    } catch (error) {
      console.error("Error deleting approver Module:", error);
    }
  };

  // Memoize the context value
  const value = useMemo(
    () => ({
      isLoading,
      error,
      pagination,
      approvalLevels,
      addApprovalLevel,
      updateApprovalLevel,
      deleteApprovalLevel,
      fetchApprovalLevel,
    }),
    [approvalLevels, isLoading] 
  );

  return <ApprovalLevelMasterContext.Provider value={value}>{children}</ApprovalLevelMasterContext.Provider>;
};
